import type { Pagination as PaginationInfo } from '@/types/anime';

interface PaginationProps {
	pagination: PaginationInfo;
	onPageChange: (page: number) => void;
	disabled?: boolean;
}

/** Tạo dãy trang có dấu "…" quanh trang hiện tại (API có tới hơn 1.000 trang). */
function buildPageItems(current: number, total: number): Array<number | 'gap'> {
	if (total <= 7) {
		return Array.from({ length: total }, (_, index) => index + 1);
	}

	const items = new Set<number>([1, total, current]);
	for (const offset of [-2, -1, 1, 2]) {
		const page = current + offset;
		if (page > 1 && page < total) items.add(page);
	}

	const sorted = [...items].sort((a, b) => a - b);
	const result: Array<number | 'gap'> = [];
	let previous = 0;

	for (const page of sorted) {
		if (previous && page - previous > 1) result.push('gap');
		result.push(page);
		previous = page;
	}
	return result;
}

export function Pagination({
	pagination,
	onPageChange,
	disabled = false,
}: PaginationProps) {
	const { currentPage, totalPages, totalItems } = pagination;
	if (totalPages <= 1) return null;

	const items = buildPageItems(currentPage, totalPages);
	const buttonBase =
		'min-w-9 rounded-lg border border-line px-3 py-2 text-sm transition disabled:cursor-not-allowed disabled:opacity-40';

	return (
		<nav
			className="mt-7 flex flex-wrap items-center justify-center gap-2"
			aria-label="Phân trang"
		>
			<button
				type="button"
				className={`${buttonBase} text-muted hover:border-accent hover:text-ink`}
				onClick={() => onPageChange(currentPage - 1)}
				disabled={disabled || currentPage <= 1}
				aria-label="Trang trước"
			>
				‹
			</button>

			{items.map((item, index) =>
				item === 'gap' ? (
					<span
						key={`gap-${index}`}
						className="px-1 text-muted"
						aria-hidden="true"
					>
						…
					</span>
				) : (
					<button
						key={item}
						type="button"
						className={
							item === currentPage
								? `${buttonBase} border-accent bg-accent/15 font-bold text-ink`
								: `${buttonBase} text-muted hover:border-accent hover:text-ink`
						}
						onClick={() => onPageChange(item)}
						disabled={disabled}
						aria-current={item === currentPage ? 'page' : undefined}
					>
						{item}
					</button>
				),
			)}

			<button
				type="button"
				className={`${buttonBase} text-muted hover:border-accent hover:text-ink`}
				onClick={() => onPageChange(currentPage + 1)}
				disabled={disabled || currentPage >= totalPages}
				aria-label="Trang sau"
			>
				›
			</button>

			<span className="ml-2 w-full text-center text-xs text-muted min-[700px]:w-auto">
				{totalItems.toLocaleString('vi-VN')} tựa · trang {currentPage}/
				{totalPages}
			</span>
		</nav>
	);
}
